import './MembersPanel.css';

import { useEffect, useMemo, useState } from 'react';
import { useMembershipMembersByType, useMembershipTypes } from '../../queries';
import MembersSearchBar from './MembersSearchBar';
import MembersStatusFilter, { type MembersStatusFilterValue } from './MembersStatusFilter';
import MembersTierFilter from './MembersTierFilter';
import MembersTable from './MembersTable';
import IssueMembershipByPhoneSheet from './IssueMembershipByPhoneSheet';

type Props = {
  membershipEnabled: boolean;
  onOpenFullProfile: (membershipUuid: string) => void;
  /**
   * When `true`, the IssueMembershipByPhoneSheet is mounted in the open state
   * on first render. Used by the Members-tab header CTA and the no-members
   * empty state CTA via the `?issue=1` URL flag.
   */
  initialIssueOpen?: boolean;
  onIssueSheetClose?: () => void;
};

const PAGE_SIZE = 20;

const mapStatusToServer = (value: MembersStatusFilterValue): string => {
  switch (value) {
    case 'active':
      return 'ACTIVE';
    case 'expired':
      return 'EXPIRED';
    case 'expiring':
      return 'EXPIRING';
    case 'all':
    default:
      return '';
  }
};

const MembersPanel = ({
  membershipEnabled,
  onOpenFullProfile,
  initialIssueOpen,
  onIssueSheetClose,
}: Props) => {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<MembersStatusFilterValue>('all');
  const [tierUuid, setTierUuid] = useState('');
  const [page, setPage] = useState(0);
  const [issueOpen, setIssueOpen] = useState(Boolean(initialIssueOpen));
  const [issueBanner, setIssueBanner] = useState<string | null>(null);

  // Honor a delayed-open trigger from the parent (header CTA).
  useEffect(() => {
    if (initialIssueOpen) setIssueOpen(true);
  }, [initialIssueOpen]);

  // Clear the success banner after 4s.
  useEffect(() => {
    if (!issueBanner) return;
    const id = window.setTimeout(() => setIssueBanner(null), 4000);
    return () => window.clearTimeout(id);
  }, [issueBanner]);

  const typesQuery = useMembershipTypes(membershipEnabled);
  const tiers = typesQuery.data?.items ?? [];

  const membersQuery = useMembershipMembersByType({
    typeUuid: tierUuid || undefined,
    status: mapStatusToServer(status),
    query: search.trim() || undefined,
    page,
    pageSize: PAGE_SIZE,
    enabled: membershipEnabled,
  });

  const themeTokenByTypeUuid = useMemo(() => {
    const map: Record<string, string> = {};
    for (const tier of tiers) {
      map[tier.membershipTypeUuid] = tier.themeColorToken;
    }
    return map;
  }, [tiers]);

  const members = useMemo(() => membersQuery.data?.items ?? [], [membersQuery.data?.items]);
  const totalCount = membersQuery.data?.totalElements ?? null;
  const totalPages = membersQuery.data?.totalPages ?? 0;

  return (
    <div className="members-panel">
      <div className="members-panel__controls">
        <MembersSearchBar
          value={search}
          onChange={(next) => {
            setSearch(next);
            setPage(0);
          }}
        />
        <div className="members-panel__filters">
          <MembersStatusFilter
            value={status}
            onChange={(next) => {
              setStatus(next);
              setPage(0);
            }}
            totalCount={totalCount}
          />
          <MembersTierFilter
            value={tierUuid}
            onChange={(next) => {
              setTierUuid(next);
              setPage(0);
            }}
            tiers={tiers}
          />
        </div>
      </div>

      <MembersTable
        members={members}
        themeTokenByTypeUuid={themeTokenByTypeUuid}
        isLoading={membersQuery.isLoading}
        onView={onOpenFullProfile}
      />

      {totalPages > 1 ? (
        <div className="members-panel__pagination">
          <button
            type="button"
            className="members-panel__page-btn"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            Previous
          </button>
          <span className="members-panel__page-indicator">
            Page {page + 1} of {totalPages}
          </span>
          <button
            type="button"
            className="members-panel__page-btn"
            onClick={() => setPage((p) => p + 1)}
            disabled={!membersQuery.data?.hasNext}
          >
            Next
          </button>
        </div>
      ) : null}

      <IssueMembershipByPhoneSheet
        isOpen={issueOpen}
        onClose={() => {
          setIssueOpen(false);
          onIssueSheetClose?.();
        }}
        onIssued={(result) => {
          setIssueBanner(
            result.customerCreated
              ? 'Customer created and membership issued.'
              : 'Membership attached to existing customer.'
          );
        }}
      />

      {issueBanner ? (
        <div
          className="members-panel__banner"
          role="status"
          data-testid="members-issue-banner"
        >
          {issueBanner}
        </div>
      ) : null}
    </div>
  );
};

export default MembersPanel;
